/** Ступени XP по ТЗ FreelanCYX (заказчик / исполнитель) */

export const XP_RING_CAP = 20000;

/** С этим XP — лига «Магнат/Киборг» и золотое кольцо профиля (как на бэкенде, 20001+) */
export const XP_MAGNATE_RING_MIN = 20001;

export type XpTierInfo = {
  label: string;
  /** Нижняя граница текущей ступени (включительно) */
  lo: number;
  /** Верхняя граница (включительно); null = без потолка (последняя лига) */
  hi: number | null;
  /** 0..1 — прогресс внутри текущей ступени (для прочих UI) */
  pctInTier: number;
};

export type XpTierModalRow = {
  title: string;
  range: string;
  description: string;
};

const CUSTOMER_BANDS: Array<{ lo: number; hi: number; label: string }> = [
  { lo: 0, hi: 100, label: '🐣 Новичок' },
  { lo: 101, hi: 500, label: '🌱 Стартапер' },
  { lo: 501, hi: 2000, label: '💼 Партнер' },
  { lo: 2001, hi: 7000, label: '👔 Инвестор' },
  { lo: 7001, hi: 20000, label: '🦈 Акула' }
];

const EXECUTOR_BANDS: Array<{ lo: number; hi: number; label: string }> = [
  { lo: 0, hi: 100, label: '🥚 Скаут' },
  { lo: 101, hi: 500, label: '🔨 Мастер' },
  { lo: 501, hi: 2000, label: '⚙️ Профи' },
  { lo: 2001, hi: 7000, label: '💎 Эксперт' },
  { lo: 7001, hi: 20000, label: '🏆 Топ-тир' }
];

/** Индекс ступени 0..5 — совпадает с бэкендом (для модалки «текущий») */
export function xpTierStepIndex(xp: number): number {
  const x = Math.round(Number(xp) || 0);
  if (x >= 20001) return 5;
  if (x >= 7001) return 4;
  if (x >= 2001) return 3;
  if (x >= 501) return 2;
  if (x >= 101) return 1;
  return 0;
}

/** 0..1 — линейно по XP_RING_CAP (для прочих шкал); визуальное кольцо — см. xpToRingFraction */
export function xpRingFillPct(xp: number): number {
  const x = Math.max(0, Math.round(Number(xp) || 0));
  return Math.min(1, x / XP_RING_CAP);
}

/** Подпись по дуге кольца */
export function xpRingArcCaption(xp: number): string {
  const x = Math.round(Number(xp) || 0);
  return `XP: ${x}/${XP_RING_CAP}`;
}

/** Риски на кольце (пороги XP / XP_RING_CAP), без цифр */
export const XP_RING_TICK_XP = [100, 500, 2000, 7000, 20000] as const;

export type XpRingNeonSegment = {
  fromXp: number;
  toXp: number;
  stroke: string;
  glow: string;
};

const XP_RING_NEON_BRACKETS: Array<{ lo: number; hi: number }> = [
  { lo: 0, hi: 100 },
  { lo: 100, hi: 500 },
  { lo: 500, hi: 2000 },
  { lo: 2000, hi: 7000 },
  { lo: 7000, hi: XP_RING_CAP }
];

/**
 * Доля полного кольца (0..1): пять равных дуг, как риски; каждая дуга = интервал XP выше.
 * Линейная шкала 0..20000 по кругу давала бы 95 XP ≈ 0,5% — дуга не видна.
 */
export function xpToRingFraction(xp: number): number {
  const x = Math.max(0, Math.min(Number(xp) || 0, XP_RING_CAP));
  const n = XP_RING_NEON_BRACKETS.length;
  const slice = 1 / n;
  let acc = 0;
  for (const { lo, hi } of XP_RING_NEON_BRACKETS) {
    if (x <= lo) break;
    if (x >= hi) {
      acc += slice;
    } else {
      const span = hi - lo;
      acc += span > 0 ? ((x - lo) / span) * slice : 0;
      break;
    }
  }
  return Math.min(1, acc);
}

/** Палитра: зелёный → жёлтый → голубой → фиолетовый → оранжевый; от 20000 — золото */
const NEON_PALETTE: Array<{ stroke: string; glow: string }> = [
  { stroke: '#5dffb4', glow: 'rgba(70, 255, 160, 0.98)' },
  { stroke: '#ffef5a', glow: 'rgba(255, 238, 90, 0.98)' },
  { stroke: '#62eeff', glow: 'rgba(80, 235, 255, 0.98)' },
  { stroke: '#d09aff', glow: 'rgba(200, 160, 255, 0.98)' },
  { stroke: '#ff9f52', glow: 'rgba(255, 155, 85, 0.98)' }
];

const GOLD_NEON = { stroke: '#ffd98c', glow: 'rgba(255, 215, 135, 0.99)' };

/**
 * Участки заполненной дуги: каждый порог 0–100, 100–500, … подсвечен своим неоном;
 * внутри текущего интервала длина растёт плавно вместе с XP.
 */
export function xpRingNeonSegments(xpInput: number): XpRingNeonSegment[] {
  const xp = Math.max(0, Number(xpInput) || 0);

  if (xp >= XP_MAGNATE_RING_MIN) {
    return [
      {
        fromXp: 0,
        toXp: XP_RING_CAP,
        stroke: GOLD_NEON.stroke,
        glow: GOLD_NEON.glow
      }
    ];
  }

  const filledXp = Math.min(xp, XP_RING_CAP);
  const out: XpRingNeonSegment[] = [];

  for (let i = 0; i < XP_RING_NEON_BRACKETS.length; i++) {
    const { lo, hi } = XP_RING_NEON_BRACKETS[i];
    if (filledXp <= lo) break;
    const end = Math.min(filledXp, hi);
    if (end <= lo) continue;

    const isLast = i === XP_RING_NEON_BRACKETS.length - 1;
    const palette = isLast && end >= XP_RING_CAP ? GOLD_NEON : NEON_PALETTE[i];

    out.push({
      fromXp: lo,
      toXp: end,
      stroke: palette.stroke,
      glow: palette.glow
    });
  }

  return out;
}

/** strokeDasharray: дуга в долях окружности через xpToRingFraction (согласовано с рисками) */
export function xpRingSegmentDashArray(fromXp: number, toXp: number, circumference: number): string {
  const fromF = xpToRingFraction(fromXp);
  const toF = xpToRingFraction(toXp);
  const len = Math.max(0, (toF - fromF) * circumference);
  return `0 ${fromF * circumference} ${len} ${(1 - toF) * circumference}`;
}

export const XP_TIER_MODAL_CUSTOMER: XpTierModalRow[] = [
  {
    title: '🐣 Новичок',
    range: '0 – 100 XP',
    description: 'Стартовая ступень на бирже.'
  },
  {
    title: '🌱 Стартапер',
    range: '101 – 500 XP',
    description: 'Начал активно формировать команду.'
  },
  {
    title: '💼 Партнер',
    range: '501 – 2000 XP',
    description: 'Проверенный временем и заказами.'
  },
  {
    title: '👔 Инвестор',
    range: '2001 – 7000 XP',
    description: 'Серьёзные бюджеты, крупные проекты.'
  },
  {
    title: '🦈 Акула',
    range: '7001 – 20000 XP',
    description: 'Один из лидеров биржи.'
  },
  {
    title: '🏛️ Магнат',
    range: '20001+ XP',
    description: 'Высшая лига, безупречная репутация.'
  }
];

export const XP_TIER_MODAL_EXECUTOR: XpTierModalRow[] = [
  {
    title: '🥚 Скаут',
    range: '0 – 100 XP',
    description: 'Собирает портфолио, учится.'
  },
  {
    title: '🔨 Мастер',
    range: '101 – 500 XP',
    description: 'Стабильно закрывает задачи.'
  },
  {
    title: '⚙️ Профи',
    range: '501 – 2000 XP',
    description: 'Работает быстро и без косяков.'
  },
  {
    title: '💎 Эксперт',
    range: '2001 – 7000 XP',
    description: 'Решает уникальные, дорогие задачи.'
  },
  {
    title: '🏆 Топ-тир',
    range: '7001 – 20000 XP',
    description: 'Входит в элиту фрилансеров.'
  },
  {
    title: '🦾 Киборг',
    range: '20001+ XP',
    description: 'Машина по закрытию сложных проектов.'
  }
];

function tierFromBands(xRounded: number, bands: Array<{ lo: number; hi: number; label: string }>, topLabel: string): XpTierInfo {
  if (xRounded >= 20001) {
    return { label: topLabel, lo: 20001, hi: null, pctInTier: 1 };
  }
  if (xRounded < 0) {
    const b0 = bands[0];
    return { label: b0.label, lo: b0.lo, hi: b0.hi, pctInTier: 0 };
  }

  for (const b of bands) {
    if (xRounded >= b.lo && xRounded <= b.hi) {
      const span = b.hi - b.lo + 1;
      const pct = span > 0 ? (xRounded - b.lo) / span : 0;
      return {
        label: b.label,
        lo: b.lo,
        hi: b.hi,
        pctInTier: Math.min(1, Math.max(0, pct))
      };
    }
  }

  return { label: topLabel, lo: 20001, hi: null, pctInTier: 1 };
}

export function xpTierCustomer(xp: number): XpTierInfo {
  return tierFromBands(Math.round(Number(xp) || 0), CUSTOMER_BANDS, '🏛️ Магнат');
}

export function xpTierExecutor(xp: number): XpTierInfo {
  return tierFromBands(Math.round(Number(xp) || 0), EXECUTOR_BANDS, '🦾 Киборг');
}

export function xpTierForProfileRole(xp: number, role: string | null | undefined): XpTierInfo {
  const r = String(role || '').trim();
  if (r === 'executor') return xpTierExecutor(xp);
  if (r === 'customer') return xpTierCustomer(xp);
  return xpTierCustomer(xp);
}

export function xpTierModalRowsForRole(role: string | null | undefined): XpTierModalRow[] {
  const r = String(role || '').trim();
  return r === 'executor' ? XP_TIER_MODAL_EXECUTOR : XP_TIER_MODAL_CUSTOMER;
}

export function xpTierModalHeading(role: string | null | undefined): string {
  const r = String(role || '').trim();
  return r === 'executor' ? '🛠️ Статусы исполнителя' : '📋 Статусы заказчика';
}
